import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
	createEntityDefinition,
	EntityActionFactory,
	EntityCache,
	EntityCollectionServiceFactory,
	EntityDefinition,
	EntityDefinitionService,
	EntityOp,
} from "@ngrx/data";
import { Store } from "@ngrx/store";
import {
	ApiPayload,
	ApiResponse,
	ListingSummary,
	RestService,
	UiComponent,
} from "@recore-solutions/nstar-lib";
import { combineLatest, Observable, Subject } from "rxjs";
import { bufferTime, filter, map, mergeMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { getListingSummaryPropertyTypeCD } from "../shared/interfaces/listing-summary";
import { dispatchRelatedRecordActions } from "../shared/shared.functions";
import { sortByPropertyAlphabetic } from "../shared/sort-fns";

type ListingOrganizationAndType = Pick<ListingSummary, "listingOrganizationSK" | "propertyTypeCD">;
interface TableAndSchema {
	table: string;
	schema: string;
	listingSummary: ListingOrganizationAndType;
}

@Injectable({
	providedIn: "root",
})
export class CodeTableService {
	batchedCodeTables: Subject<TableAndSchema> = new Subject();
	batchedCodeTable$;

	requestedTables: {
		[table: string]: { [organizationSK: number]: { [propertyTypeCD: string]: Date } };
	} = {};
	codeTableServices: { [table: string]: any } = {};

	constructor(
		private store: Store<EntityCache>,
		private entityActionFactory: EntityActionFactory,
		private entityDefinitionService: EntityDefinitionService,
		private entityCollectionServiceFactory: EntityCollectionServiceFactory,
		private restService: RestService
	) {
		this.batchedCodeTable$ = this.batchedCodeTables.asObservable().pipe(
			bufferTime(300, null, 100),
			filter((tables) => !!tables.length),
			map((tables) => tables.filter(this.isUnique)),
			mergeMap(this.getTable$),
			tap((payloads) => payloads.forEach((payload) => this.dispatchRelatedRecordActions(payload)))
		);
		this.batchedCodeTable$.subscribe();
	}

	requestTable = (
		table: string,
		schema: string,
		listingSummary: ListingOrganizationAndType,
		uiComponent: UiComponent<any>
	) => {
		// If this table has never been requested before, then create an entry for it.
		let requestedTable = this.requestedTables[table];
		if (!requestedTable) {
			requestedTable = this.requestedTables[table] = {};
		}
		let organizationTable = requestedTable[listingSummary.listingOrganizationSK];
		if (!organizationTable) {
			organizationTable = requestedTable[listingSummary.listingOrganizationSK] = {};
		}
		// If the table has not had requests for
		if (!Object.keys(organizationTable).includes(listingSummary.propertyTypeCD)) {
			this.batchedCodeTables.next({ table, schema, listingSummary });
			organizationTable[listingSummary.propertyTypeCD] = new Date();
			this.registerTableEntityDefinition(
				this.createTableEntityDefinition(table, [
					"organizationSK",
					"propertyTypeCD",
					uiComponent.applicationPropertyName,
				])
			);
		}
		const service = this.entityCollectionServiceFactory.create(table);
		service.setFilter(listingSummary);
		return service.filteredEntities$;
	};

	// If not already registered, register the EntityDefinition.
	registerTableEntityDefinition = (entityDefinition: EntityDefinition): void => {
		if (!this.entityDefinitionService.getDefinition(entityDefinition.entityName, false)) {
			this.entityDefinitionService.registerDefinition(entityDefinition);
		}
	};

	// Create a new entity definition for a code table.
	createTableEntityDefinition = <T = any>(
		table: string,
		props: string[] | string
	): EntityDefinition<T> => {
		const idProps: string[] = Array.isArray(props) ? props : [props];
		const entityDefinition = createEntityDefinition({
			entityName: table,
			selectId: (entity: any) => idProps.map((idProp) => entity[idProp]).join(),
			filterFn: this.filterCodesByListingSummary,
			sortComparer: this.codeTableSortFn(table),
		});
		return entityDefinition;
	};

	filterCodesByListingSummary = (codes: any[], listingSummary: ListingSummary): any[] => {
		return codes.filter(this.filterCodeTableValuesByPropertyTypeColumn(listingSummary));
	};

	filterCodeTableValuesByPropertyTypeColumn = (listingSummary: ListingSummary) => {
		if (!listingSummary?.propertyTypeCD) {
			return () => false;
		}
		const propertyTypeCD = getListingSummaryPropertyTypeCD(listingSummary);
		return (row: any) => {
			// If an organization SK is set and does not match, return false;
			if (!!row.organizationSK && row.organizationSK !== listingSummary.listingOrganizationSK) {
				return false;
			}
			// If there is a property type set and it does not match, return false;
			if (!!row.propertyTypeCD && row.propertyTypeCD !== propertyTypeCD) {
				return false;
			}
			return true;
		};
	};

	dispatchRelatedRecordActions = (
		payload: ApiPayload<any, any[]>,
		entityOp: EntityOp = EntityOp.ADD_MANY
	): void => {
		// We also need to dispatch the main records, so let's add it to the related.
		if (!!payload?.records?.length) {
			payload.related.push({
				recordType: payload.recordType,
				metadata: {} as any,
				records: payload.records,
			});
			payload.recordType = null;
			payload.records = [];
		}
		if (!!this.store && !!this.entityActionFactory) {
			dispatchRelatedRecordActions(this.store, this.entityActionFactory, entityOp)(payload);
		}
	};

	isUnique = (tableSchema: TableAndSchema, i: number, arr: TableAndSchema[]): boolean => {
		return (
			i === arr.findIndex((t) => t.table === tableSchema.table && t.schema === tableSchema.schema)
		);
	};

	getTable$ = (tableSchemas: TableAndSchema[]): Observable<ApiPayload<any, any[]>[]> => {
		const schemaAndTable: { [schema: string]: string[] } = {};
		let organizationSK: number;
		let propertyTypeCD: string;
		tableSchemas.forEach((ts) => {
			if (!schemaAndTable[ts.schema]) {
				schemaAndTable[ts.schema] = [];
			}
			schemaAndTable[ts.schema].push(ts.table);
			organizationSK = ts.listingSummary.listingOrganizationSK;
			propertyTypeCD = ts.listingSummary.propertyTypeCD;
		});
		const requests: Observable<ApiPayload<any, any[]>>[] = [];
		Object.entries(schemaAndTable).forEach(([tableSchemaName, tables]: [string, string[]]) => {
			let tableName: string;
			tables.forEach((table) => {
				tableName = !tableName ? table : [tableName, table].join(",");
			});
			const httpParams = new HttpParams({
				fromObject: {
					tableSchemaName,
					tableName,
					procName: "Polaris.spGetDynamicCodeDataAPI",
					sinceDate: "1900-01-01",
					pageNumber: "1",
					pageSize: "500",
					Organization_SK: organizationSK,
					PropertyType_CD: propertyTypeCD,
				},
			});
			requests.push(this.getCodeTables(httpParams));
		});
		return combineLatest(requests);
	};

	getCodeTables = <T, S = any[]>(httpParams: HttpParams): Observable<ApiResponse<T, S>> => {
		return this.restService.get<ApiResponse<T, S>>(environment.endpoints.codeTables, httpParams);
	};

	codeTableSortFn = (tableName: string) => {
		switch (tableName) {
			case "BuilderAssociation":
				return sortByPropertyAlphabetic("associationName");
		}

		return (a: { sortOrder?: number }, b: { sortOrder?: number }): number => {
			if (a.hasOwnProperty("sortOrder") || b.hasOwnProperty("sortOrder")) {
				return (a?.sortOrder || 0) - (b?.sortOrder || 0);
			}
			const nameColumn = this.toCamelCase(tableName) + "Name";
			if (a?.hasOwnProperty(nameColumn) || b?.hasOwnProperty(nameColumn)) {
				return ((a?.[nameColumn] || "") as string).localeCompare(b?.[nameColumn] || "");
			}
			return 0;
		};
	};

	toCamelCase(str: string): string {
		return str
			.replace(/(?:^\w|[A-Z]|\b\w)/g, (ltr, idx) =>
				idx === 0 ? ltr.toLowerCase() : ltr.toUpperCase()
			)
			.replace(/\s+/g, "");
	}
}
